<template>
  <div>
    <v-container class="mb-0">
      <v-layout row wrap class="mb-0">
        <v-flex xs12>
          <v-layout row wrap align-center>
            <v-icon class="mr-3" @click="$router.go(-1)">arrow_back</v-icon>
            <h1 class="primary--text mr-3 d-inline" v-if="aid">Response to {{ aid.event.title }}</h1>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>

    <v-container grid-list-xl>
      <app-alert
        class="mt-4 mb-1"
        type="success"
        message="Aid successfully created."
        :dismissible="true"
        v-if="$route.query.newAid"
      />

      <!-- Aid Information -->
      <v-layout row wrap>
        <v-flex xs12>
          <v-card>
            <v-card-title class="subheading font-weight-bold px-4">
              <span v-if="aid">Response to {{ aid.event.title }}</span>
              <v-spacer></v-spacer>
              <v-btn
                small
                depressed
                color="accent"
                @click="editAid = true"
                class="ma-0"
                v-if="aid && canEditAid"
              >
                <v-icon size="14" class="pr-1">edit</v-icon>Edit Aid
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-container class="px-4" grid-list-xl>
              <v-layout row wrap v-if="aid">
                <v-flex xs12 sm6 md4>
                  <b>Description</b> <br>
                  <span> <b>{{ aid.category.name }}</b> - {{ aid.description }}</span>
                  <br />
                  <small class="grey--text">Beneficiaries: {{ aid.count.toLocaleString() }}</small>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Event</b>
                  <br />
                  <span>
                    <router-link :to="'/events/' + aid.event_id">{{ aid.event.title }}</router-link>
                  </span>
                  <br />
                  <small class="grey--text">Affected: {{ aid.event.affected.toLocaleString() }}</small>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Location</b>
                  <p>
                    {{ aid.location.name }}, {{ aid.location.tag }},
                    <router-link :to="`/aids?s=${aid.state.name}`">{{ aid.state.name }}</router-link>
                  </p>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Period</b>
                  <p>{{ formatDate(aid.timestamp) }} - {{ formatDate(aid.timestamp_end) }}</p>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Funding Partner</b>
                  <br />
                  <span>{{ aid.agency.name }} (Value: {{ formatCurrency(aid.value) }})</span>
                  <br />
                  <small class="grey--text">Sponsored by: {{ aid.sponsor }}</small>
                </v-flex>

                <v-flex xs12 sm6 md4>
                  <b>Status</b>
                  <p>
                    <v-chip
                      small
                      :color="aid.status === 'completed' ? 'green' : 'red'"
                      text-color="white"
                      class="mx-0"
                    >{{ $lang(aid.status) }}</v-chip>
                  </p>
                </v-flex>
              </v-layout>
              <v-layout row wrap justify-center class="pa-5" v-else>
                <v-progress-circular :indeterminate="true" :size="32" :width="3" v-if="isLoading"></v-progress-circular>
              </v-layout>
            </v-container>
          </v-card>
        </v-flex>
      </v-layout>

      <EditAid
        v-if="aid && canEditAid"
        :isOpen="editAid"
        :aidData="aid"
        @closed="editAid = false"
        @saved="updateAid"
      />

      <!-- Beneficiaries -->
      <v-layout row wrap v-if="aid">
        <v-flex xs12>
          <v-card>
            <v-card-title class="subheading font-weight-bold px-4">
              <span>Aid Beneficiaries Breakdown</span>
              <v-spacer></v-spacer>
              <v-btn
                small
                depressed
                color="accent"
                @click.stop="addBeneficiary = true"
                class="ma-0"
                v-if="canEditAid"
              >
                <v-icon small>add</v-icon>Add Beneficiary
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-layout row wrap justify-center class="pa-5" v-if="isLoadingCategories">
              <v-progress-circular :indeterminate="true" :size="32" :width="3"></v-progress-circular>
            </v-layout>
            <v-data-table
              :headers="beneficiariesHeaders"
              :items="beneficiaries"
              :loading="isLoading"
              :hide-actions="beneficiaries.length < 5"
              no-data-text="No beneficiaries for this aid"
              v-else
            >
              <template v-slot:items="props">
                <td>{{ (props.index + 1) }}</td>
                <td>{{ beneficiaryName(props.item.beneficiary_id) }}</td>
                <td>{{ props.item.value }}</td>
                <td>
                  <v-icon
                    small
                    @click="deleteRow(props.item._id, 'beneficiaries', 'aid_beneficiary')"
                    v-if="canEditAid"
                  >delete</v-icon>
                </td>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>

      <AddPopulationGroup
        v-if="aid && canEditAid"
        :isOpen="addBeneficiary"
        :type="'aid'"
        :typeId="aid._id"
        :agent="currentUser._id"
        :beneficiaries="beneficiaries"
        @closed="addBeneficiary = false"
        @saved="updateBeneficiaries"
      />

      <!-- Key Lessons -->
      <v-layout row wrap v-if="aid">
        <v-flex xs12>
          <v-card>
            <v-card-title class="subheading font-weight-bold px-4">
              <span>Key Lessons</span>
              <v-spacer></v-spacer>
              <v-btn
                small
                depressed
                color="accent"
                @click="addLesson = true"
                class="ma-0"
                v-if="canEditAid"
              >
                <v-icon small>add</v-icon>Add Lesson
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-data-table
              :headers="lessonsHeaders"
              :items="lessons"
              :loading="isLoading"
              :hide-actions="lessons.length < 5"
              no-data-text="No key lessons for this Intervention"
            >
              <template v-slot:items="props">
                <td>{{ (props.index + 1) }}</td>
                <td class="py-3">{{ formatDate(props.item.created) }}</td>
                <td class="py-3">{{ props.item.description }}</td>
                <td>
                  <v-icon
                    small
                    @click="deleteRow(props.item._id, 'challenges', 'challenge')"
                    v-if="canEditAid"
                  >delete</v-icon>
                </td>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>

      <AddLesson
        v-if="aid && canEditAid"
        :isOpen="addLesson"
        :aid="aid._id"
        :agent="currentUser._id"
        @closed="addLesson = false"
        @saved="updateLessons"
      />
    </v-container>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { setFormData } from '@/utils/http'
import { FETCH_CATEGORY } from '@/store/actions/category'
import rolesData from '@/data/roles'

import AddPopulationGroup from '@/components/Modals/AddPopulationGroup.vue'
import AddLesson from '@/components/Modals/AddLesson.vue'
import EditAid from './components/EditAid.vue'

export default {
  name: 'Aid',
  props: ['id'],
  components: {
    AddPopulationGroup,
    AddLesson,
    EditAid
  },
	created () {
    this.isLoading = true
    this.isLoadingCategories = false

    // fetch all beneficiary categories in state
    this.$store.dispatch(FETCH_CATEGORY, { model: 'beneficiary' })
      .finally(() => (this.isLoadingCategories = false))
      
		// fetch this aid and set related data
		const params = setFormData({ _id: this.id })
		this.$http.post('aid/read_with_details', params)
			.then(({ data: response }) => {
				if (response.code === 200) {
          this.aid = response.data
          if (!this.canViewAid) {
            this.$router.push({ name: 'aids' })
          }
          this.beneficiaries = this.aid.beneficiaries || []
          this.lessons = this.aid.challenges || []
				}
				else {
					this.$router.push({ name: 'aids' })
				}
			})
			.finally(() => (this.isLoading = false))
	},
	data () {
		return {
      aid: null,
      editAid: false,
      
      beneficiaries: [],
      addBeneficiary: this.$route.query.newAid || false,
			beneficiariesHeaders: [
        { text: '#', value: 'index', sortable: false },
				{ text: 'Category', value: 'beneficiary_id', sortable: false },
        { text: 'Value', value: 'value', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      
      addLesson: false,
			lessons: [],
			lessonsHeaders: [
        { text: '#', value: 'index', sortable: false },
				{ text: 'Date', value: 'created' },
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      
      isLoading: false,
      isLoadingCategories: false
		}
	},
	computed: {
    canEditAid () {
      return this.$can('create_aid') && 
      (this.currentUser._id === this.aid.agent._id || rolesData.heads.includes(this.currentUser.role))
    },
    canViewAid () {
      if (!this.$can('view_aid')) return false
      else if (this.currentUser.role === rolesData.admin) return true
      else if (this.currentUser.role === rolesData.stateHead && this.currentUser.parent_id === this.aid.state_id) return true
      else if (this.currentUser.role === rolesData.agencyHead && this.currentUser.parent_id === this.aid.agency_id) return true
      else if (this.currentUser._id === this.aid.agent_id) return true
      return false
    },
    beneficiaryCategories () { // list fetched beneficiary categories
      return this.$store.getters.categories('beneficiary') || []
    },
    ...mapGetters(['currentUser'])
  },
  methods: {
		formatDate (timestamp) {
			return (new Date(timestamp * 1000)).toLocaleDateString(
				'en-US', { year: 'numeric', month: 'long', day: 'numeric' }
			)
    },
    formatCurrency (value) {
      const currency = new Intl.NumberFormat(
      'en-US', 
        { minimumFractionDigits: 2 }
      ).format(value)

      return '₦' + currency
    },
    updateAid (payload) {
      this.aid = Object.assign(this.aid, payload)
    },
    beneficiaryName (id) {
      return this.$store.getters.categoryName('beneficiary', id)
    },
    updateBeneficiaries (payload) {
      this.beneficiaries.push(payload)
    },
    updateLessons (payload) {
      this.lessons.push(payload)
    },
    deleteRow (id, data, model) {
      const params = setFormData({ _id: id })
      this.$http.post(model + '/delete', params)
        .then(({ data: response }) => {
          if (response.code === 200) {
            this[data] = this[data].filter(item => item._id !== id)
          }
        })
    }
  }
}
</script>
